import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, useParams, Outlet, Link } from "react-router-dom";
import { Menu, ConfigProvider, Breadcrumb, Dropdown, Space, Button, message } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import api from "@/api";
import { findDeep, getReleaseDetail, format, flattenObjectArray, getType } from "@/utils";
import { publicPath } from "@/utils/base";

import dataJson from "@/../public/data.json";

const prefix = "appDetail";
import "./index.less";
import { cloneDeep } from "lodash-es";
import store from "@/stores";

function AppDetail() {
  const { add, app_detail, release, req_err, staging } = dataJson;
  const [dropdownItems, setDropdownItems] = useState<any[]>([]);
  const [appList, setAppList] = useState<any[]>([]);
  const [dropdownSelect, setDropdownSelect] = useState("");
  const [isAddVersion, setIsAddVersion] = useState<boolean>(true);
  const [isTampered, setIsTampered] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const menuKey = useRef<string>("");

  const releaseDetail = useSelector((state: any) => state.releaseDetail);
  const appTypes = useSelector((state: any) => state.appTypes);

  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const { type, id } = useParams();

  const typeObj: any = app_detail.type_obj;
  const typeItem = typeObj[getType(type!)];

  const onClick = (e: any) => {
    if (menuKey.current === e.key) {
      return;
    }
    menuKey.current = e.key;
    const targetId =
      e.key === "versionmanage"
        ? releaseDetail?.histories?.[0]?.id || id
        : releaseDetail?.currentId || id;
    navigate(`${publicPath}${e.item.props.url}/${type}/${targetId}`);
  };

  const dropdownOnClick = (e: any) => {
    setDropdownSelect(e.key);
    navigate(`${publicPath}/app_detail/versionmanage/${type}/${e.key}`);
  };

  const upgrade = async () => {
    if (!releaseDetail?.histories?.length) {
      message.error(req_err);
      return;
    }

    const firstHistory = releaseDetail.histories[0];
    const firstShowMode = findDeep(release.show_mode_arr, firstHistory?.showMode);

    // 如果有多个历史版本且可升级，直接跳转到最新版本
    if (releaseDetail.histories.length > 1 && firstShowMode?.isUpgrade) {
      handleNavigateAndRefresh(firstHistory.id);
      return;
    }

    // 否则调用升级接口
    try {
      const res: any = await api[typeItem.upgradeApi]?.({ id: releaseDetail?.currentId });
      if (res?.code === 200) {
        handleNavigateAndRefresh(res.data.id);
      } else {
        message.error(res?.msg || req_err);
      }
    } catch (error: any) {
      message.error(error?.response?.data?.msg || req_err);
    }
  };

  const handleNavigateAndRefresh = (nextId: string) => {
    navigate(`${publicPath}/app_detail/versionmanage/${type}/${nextId}`);
  };

  const getAppList = async (appTypes: any[]) => {
    try {
      const typeKeys = appTypes
        .filter((_item: any) => ["APP", "ATOMIC"].includes(_item.type))
        .map((item: any) => item.key);
      const res: any = await api.getAllNoPage({
        typeKeys,
      });
      if (res.code === 200) {
        const pathnameArr = window.location.pathname.split("/");
        const basePathname = pathnameArr.slice(0, pathnameArr.length - 2).join("/");

        const dataCopy = cloneDeep(res.data);
        if (menuKey.current === "profile") {
          const noProfile = dataCopy.filter((item: any) => {
            return !["ACS", "KCS"].includes(item.typeKey);
          });
          const list = noProfile.map((item: any) => {
            return {
              key: item.id,
              label: <Link to={`${basePathname}/${item.typeKey}/${item.firstAppId}`}>{item.name}</Link>,
            };
          });
          setAppList(list || []);
        } else {
          const list = dataCopy.map((item: any) => {
            return {
              key: item.id,
              label: <Link to={`${basePathname}/${item.typeKey}/${item.firstAppId}`}>{item.name}</Link>,
            };
          });
          setAppList(list || []);
        }
      } else {
        message.error(res.msg || req_err);
      }
    } catch (errorInfo: any) {
      console.error(errorInfo);
    }
  };

  const getAllTypes = async () => {
    const res: any = await api.getAllTypes();
    if (res.code === 200) {
      store.dispatch({
        type: "setAppTypes",
        payload: res.data,
      });
    }
  };

  // 渲染面包屑
  const renderBreadcrumb = () => (
    <div className={`${prefix}-breadCrumb`}>
      <Link to={`${publicPath}/staging`} className={`${prefix}-breadCrumb-link`}>
        {staging.app_label}
      </Link>
      &nbsp;/&nbsp;
      <Link to={`${publicPath}/app_list/app`} className={`${prefix}-breadCrumb-link`}>
        {staging.my_app}
      </Link>
      &nbsp;/&nbsp;
      <Dropdown menu={{ items: appList, style: { maxHeight: "70vh" } }}>
        <Space style={{ color: "#1A1A1A" }}>
          {releaseDetail?.histories?.[0]?.name || "--"}
          <DownOutlined />
        </Space>
      </Dropdown>
    </div>
  );

  // 渲染右上角版本管理操作
  const renderVersionManageActions = () => (
    <div className={`${prefix}-top-right`}>
      <Button type="link" disabled={isTampered || isAddVersion} onClick={upgrade}>
        {add + app_detail.version}
      </Button>
      <Dropdown
        menu={{
          items: dropdownItems,
          selectable: true,
          selectedKeys: [dropdownSelect],
          onClick: dropdownOnClick,
        }}
        overlayClassName={`${prefix}-dropdown-overlay`}
      >
        <span className={`${prefix}-dropdown`}>
          <Space>
            {app_detail.version}
            <DownOutlined />
          </Space>
        </span>
      </Dropdown>
    </div>
  );

  useEffect(() => {
    if (releaseDetail) {
      const resData = cloneDeep(releaseDetail);
      const resShowMode = findDeep(release.show_mode_arr, resData?.showMode);
      // const firstShowMode = findDeep(release.show_mode_arr, releaseDetail?.histories[0]?.showMode);
      // 有升级场景，在新版本页面，按钮要受到旧版本状态影响
      if (resShowMode?.isUpgrade && resData?.histories?.length > 1) {
        const oldShowMode = resData?.histories[1]?.showMode;
        resData.baseBtns = resShowMode?.btnsByOld[oldShowMode];
      } else {
        resData.baseBtns = resShowMode?.baseBtns;
      }
      // if (resData?.showMode === "HISTORY" || (firstShowMode?.isUpgrade && resData.id !== firstShowMode?.id)) {
      //   setIsAddVersion(false);
      // } else {
      setIsAddVersion(!resData.baseBtns?.includes("upgrade"));
      // }

      const appDetailMenu = cloneDeep(app_detail.menu);
      const menuItems = appDetailMenu.filter((item) => {
        if (typeItem.detail_menu.includes(item.key)) {
          if (item.children) {
            item.children = item.children.filter((ele) => {
              return typeItem.detail_menu.includes(ele.key);
            });
          }
          return true;
        } else {
          return false;
        }
      });
      setItems(menuItems);

      const newDropdownItems: any[] = releaseDetail?.histories?.map((item: any) => {
        const showModeItem = findDeep(release.show_mode_arr, item?.showMode);
        return {
          key: item.id,
          label: (
            <div className={`${prefix}-dropdownItems`}>
              <div className={`${prefix}-dropdownItems-version one-line-ellipsis`} title={item.version}>
                {item?.version ? (
                  <div className={`${prefix}-dropdownItems-version-text`}>
                    {app_detail.version_v}
                    {item.version}
                  </div>
                ) : (
                  <div className={`${prefix}-dropdownItems-version-text`}>{app_detail.new_version}</div>
                )}
                <div className={`${prefix}-dropdownItems-version-status`} style={{ color: showModeItem.textColor }}>
                  {showModeItem.title}
                </div>
              </div>
              <div className={`${prefix}-dropdownItems-time`}>
                {format(new Date(Number(item.createTime)), "yy年MM月dd日 HH:mm")}
              </div>
            </div>
          ),
        };
      });
      setDropdownItems(newDropdownItems || []);
    }
  }, [releaseDetail]);

  useEffect(() => {
    const pathArr = pathname.split("/");
    menuKey.current = pathArr[pathArr.length - 3];
    setDropdownSelect(id || "");
  }, [pathname]);

  useEffect(() => {
    setIsTampered(false);
    getReleaseDetail(type, id, undefined, () => {
      setIsTampered(true);
      navigate(-1);
    });
  }, [type, id]);

  useEffect(() => {
    getAllTypes();
  }, []);

  useEffect(() => {
    getAppList(appTypes);
  }, [appTypes]);
  return (
    <div className={prefix}>
      <div className={`${prefix}-menu-wrap`}>
        <ConfigProvider
          theme={{
            components: {
              Menu: {
                itemSelectedBg: "#0A59F7",
                itemSelectedColor: "#fff",
                itemBorderRadius: 0,
                iconSize: 14,
                itemHeight: 64,
                fontSize: 18,
              },
            },
          }}
        >
          <Menu
            onClick={onClick}
            mode="inline"
            items={items}
            defaultOpenKeys={["distribute", "analysis"]}
            selectedKeys={[menuKey.current]}
            className={`${prefix}-menu`}
          />
        </ConfigProvider>
      </div>
      <ConfigProvider
        theme={{
          components: {
            Button: { colorPrimary: "#0A59F7" },
            Tabs: { colorPrimary: "#0A59F7" },
          },
        }}
      >
        <div className={`${prefix}-top`}>
          {renderBreadcrumb()}
          {menuKey.current === "versionmanage" ? renderVersionManageActions() : <span />}
        </div>
        <Outlet context={{ isTampered, setIsTampered }} />
      </ConfigProvider>
    </div>
  );
}

export default AppDetail;
